import { Injectable } from '@angular/core'
import { Observable, Observer } from 'rxjs'

// in bytes, compress images larger than 1MB
const fileSizeMax = 1 * 1024 * 1024
// in pixels, compress images have the width or height larger than 1024px
const widthHeightMax = 1024
const defaultWidthHeightRatio = 1
const defaultQualityRatio = 0.7

@Injectable({
  providedIn: 'root'
})
export class CompressService {
  compress(file: File): Observable<File> {
    const imageType = file.type || 'image/jpeg'
    const reader = new FileReader()
    reader.readAsDataURL(file)

    return new Observable((observer: Observer<File>) => {
      // This event is triggered each time the reading operation is successfully completed.
      reader.onload = (ev: ProgressEvent<FileReader>) => {
        // Create an html image element
        const img = this.createImage(ev)
        // Choose the side (width or height) that longer than the other
        const imgWH = img.width > img.height ? img.width : img.height

        // Determines the ratios to compress the image
        let withHeightRatio = (imgWH > widthHeightMax) ? widthHeightMax/imgWH : defaultWidthHeightRatio
        // Fix quality ratio calculation - use defaultQualityRatio for large files
        let qualityRatio = (file.size > fileSizeMax) ? defaultQualityRatio : 0.9

        // Fires immediately after the browser loads the object
        img.onload = () => { 
          const elem = document.createElement('canvas')
          // resize width, height
          elem.width = img.width * withHeightRatio
          elem.height = img.height * withHeightRatio

          const ctx = <CanvasRenderingContext2D>elem.getContext('2d')
          ctx.drawImage(img, 0, 0, elem.width, elem.height)
          ctx.canvas.toBlob(
            // callback, called when blob created
            (blob: Blob | null) => { 
              if (blob) {
                observer.next(new File(
                  [blob],
                  file.name,
                  {
                    type: imageType,
                    lastModified: Date.now(),
                  }
                ))
                observer.complete()
              } else {
                observer.error(new Error('Failed to compress image'))
              }
            },
            imageType,
            qualityRatio, // reduce image quantity 
          )
        }

        img.onerror = () => {
          observer.error(new Error('Failed to load image'))
        }
      }

      // Catch errors when reading file
      reader.onerror = (error: ProgressEvent<FileReader>) => observer.error(error)
    })
  }

  private createImage(ev: ProgressEvent<FileReader>): HTMLImageElement {
    let imageContent = ev.target?.result as string
    const img = new Image()
    img.src = imageContent
    return img
  }
}